/**
 * One sentence about where a value came from, as wording.ts builds it (SourcePiece[]).
 *
 * Expects the pieces, the callback that opens a document by its id, and optionally `stacked`.
 *
 * Renders the pieces in a row. A piece that names a document is a button styled as a link: pressed,
 * it opens the file in DocumentPreviewModal on the page, or in a new tab when the page cannot
 * preview it (openSourceDocument in TaxProfile.tsx), so nothing on the page holds a link to a file.
 * Every other piece, the onboarding form's name included, is plain text.
 *
 * With `stacked` (the Where it came from column), each source's name goes on a line of its own:
 * the pieces are cut at every separator piece (the ', ' joinedNames puts between two names), the
 * separator is dropped, and each part is drawn as a block, so "From" stays on the first line only.
 * In that narrow column a comma between two long document names otherwise wrapped onto a line by
 * itself.
 */

import type { SourcePiece } from '@/pages/TaxProfile/wording';
import { openDocumentLabel } from '@/pages/TaxProfile/wording';

export interface SourceTextProps {
  pieces: SourcePiece[];
  onOpenDocument?: (documentId: number) => void;
  className?: string;
  /** One line per source instead of the names joined by commas. */
  stacked?: boolean;
}

/** The pieces cut at each separator piece, the separators dropped. */
const linesOf = (pieces: SourcePiece[]): SourcePiece[][] =>
  pieces.reduce<SourcePiece[][]>(
    (lines, piece) => {
      if (piece.separator) return [...lines, []];
      lines[lines.length - 1].push(piece);
      return lines;
    },
    [[]]
  );

export default function SourceText({
  pieces,
  onOpenDocument,
  className = '',
  stacked = false,
}: SourceTextProps) {
  if (pieces.length === 0) return null;
  const draw = (piece: SourcePiece, index: number) => {
    const { documentId } = piece;
    if (documentId === null) {
      // eslint-disable-next-line react/no-array-index-key
      return <span key={index}>{piece.text}</span>;
    }
    return (
      <button
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        type='button'
        className='link link-primary link-hover font-semibold'
        aria-label={openDocumentLabel(piece.text)}
        onClick={() => onOpenDocument?.(documentId)}
      >
        {piece.text}
      </button>
    );
  };
  if (stacked) {
    return (
      <span className={`flex flex-col gap-0.5 ${className}`}>
        {linesOf(pieces).map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} className='block'>
            {line.map(draw)}
          </span>
        ))}
      </span>
    );
  }
  return <span className={className}>{pieces.map(draw)}</span>;
}
